import { useState } from "react";
import { Megaphone, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const INDUSTRY_OPTIONS = [
    { key: "agency", label: "Marketing Agency" },
    { key: "advertiser", label: "Brand / Advertiser" },
    { key: "marketing", label: "Other Marketing" },
];

const BUDGET_BANDS = [
    "< $5k / mo",
    "$5k–$25k",
    "$25k–$100k",
    "$100k+",
];

export default function PartnerInquiry() {
    const [form, setForm] = useState({
        company: "",
        contact_name: "",
        role: "",
        email: "",
        industry: "agency",
        monthly_budget: "$5k–$25k",
        message: "",
        accepted_terms: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.company.trim() || !form.contact_name.trim()) {
            toast.error("Company and contact name required");
            return;
        }
        if (!form.email.includes("@")) {
            toast.error("Valid email required");
            return;
        }
        if (!form.message.trim()) {
            toast.error("Tell us about your idea");
            return;
        }
        if (!form.accepted_terms) {
            toast.error("Accept the partner terms to file");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/hub/partners", form);
            setSubmitted(true);
            toast.success("Intel filed · Director K. will be in touch");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Submission failed";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="partner-inquiry"
            className="relative bg-cream py-24 md:py-32 overflow-hidden"
            data-testid="partner-inquiry-section"
        >
            <div
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1519046904884-53103b34b206?auto=format&fit=crop&w=2000&q=80')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
                aria-hidden
            />
            <div className="relative max-w-6xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-12">
                <div className="md:col-span-5">
                    <span className="divider-rule text-brown">
                        <Megaphone size={12} /> Partner Inquiry · Marketing
                    </span>
                    <h2 className="font-serif text-5xl md:text-6xl text-forest mt-6 leading-[1.02]">
                        For agencies
                        <br />
                        <span className="italic text-gold">
                            and advertisers.
                        </span>
                    </h2>
                    <p className="mt-6 text-brown leading-relaxed max-w-md">
                        EIGOO builds independent brand platforms for
                        high-trust commerce. If your agency or brand wants
                        to partner up, file an inquiry and we'll reach out
                        when there's an opening.
                    </p>
                    <ul className="mt-10 space-y-4 text-sm text-forest">
                        {[
                            "Co-developed creative for ASMR-first cuts",
                            "Island Botanicals catalogue for affiliate splits",
                            "10% Legacy Tax baked into every co-launch",
                            "Dedicated subdomain and workspace per partner",
                        ].map((s, i) => (
                            <li key={s} className="flex items-start gap-3">
                                <span
                                    className="mt-1 w-2 h-2 rounded-full"
                                    style={{
                                        background: "var(--ig-gold)",
                                    }}
                                />
                                <span>0{i + 1} · {s}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-7">
                    {submitted ? (
                        <div
                            className="p-10 bg-forest text-cream rounded-sm relative overflow-hidden"
                            data-testid="partner-inquiry-success"
                        >
                            <div className="grain absolute inset-0" />
                            <div className="relative">
                                <ShieldCheck
                                    size={36}
                                    className="text-gold"
                                />
                                <h3 className="font-serif text-3xl mt-4">
                                    Intel received.
                                </h3>
                                <p className="mt-3 text-cream/70 leading-relaxed">
                                    Director K. will counter-sign and
                                    respond within 72 hours. Welcome to
                                    the manifest.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            className="p-8 md:p-10 bg-cream-warm/60 border border-forest/10 rounded-sm space-y-5"
                            data-testid="partner-inquiry-form"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="Company / Agency">
                                    <input
                                        type="text"
                                        value={form.company}
                                        onChange={(e) =>
                                            update("company", e.target.value)
                                        }
                                        className={inputCls}
                                        data-testid="partner-company"
                                    />
                                </Field>
                                <Field label="Contact Name">
                                    <input
                                        type="text"
                                        value={form.contact_name}
                                        onChange={(e) =>
                                            update(
                                                "contact_name",
                                                e.target.value,
                                            )
                                        }
                                        className={inputCls}
                                        data-testid="partner-contact"
                                    />
                                </Field>
                                <Field label="Role / Title">
                                    <input
                                        type="text"
                                        value={form.role}
                                        onChange={(e) =>
                                            update("role", e.target.value)
                                        }
                                        className={inputCls}
                                        placeholder="Head of Growth"
                                    />
                                </Field>
                                <Field label="Email">
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) =>
                                            update("email", e.target.value)
                                        }
                                        className={inputCls}
                                        data-testid="partner-email"
                                    />
                                </Field>
                            </div>

                            <Field label="Industry">
                                <div className="grid grid-cols-3 gap-2">
                                    {INDUSTRY_OPTIONS.map((o) => (
                                        <button
                                            type="button"
                                            key={o.key}
                                            onClick={() =>
                                                update("industry", o.key)
                                            }
                                            className={`text-[0.62rem] tracking-[0.25em] uppercase py-2 rounded-full border transition-colors ${
                                                form.industry === o.key
                                                    ? "bg-forest text-cream border-forest"
                                                    : "bg-cream text-forest border-forest/25 hover:border-forest"
                                            }`}
                                            data-testid={`partner-industry-${o.key}`}
                                        >
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                            </Field>

                            <Field label="Monthly Marketing Budget">
                                <div className="flex flex-wrap gap-2">
                                    {BUDGET_BANDS.map((b) => (
                                        <button
                                            type="button"
                                            key={b}
                                            onClick={() =>
                                                update("monthly_budget", b)
                                            }
                                            className={`text-[0.62rem] tracking-[0.2em] uppercase py-2 px-3 rounded-full border transition-colors ${
                                                form.monthly_budget === b
                                                    ? "bg-gold text-forest border-gold"
                                                    : "bg-cream text-forest border-forest/20 hover:border-gold"
                                            }`}
                                        >
                                            {b}
                                        </button>
                                    ))}
                                </div>
                            </Field>

                            <Field label="Tell us about your work — what would we build together?">
                                <textarea
                                    value={form.message}
                                    onChange={(e) =>
                                        update("message", e.target.value)
                                    }
                                    rows={4}
                                    className={inputCls}
                                    placeholder="Q1 holiday push for our portfolio of artisan brands…"
                                    data-testid="partner-message"
                                />
                            </Field>

                            <label
                                className={`flex items-start gap-3 p-4 rounded-sm border cursor-pointer transition-all ${
                                    form.accepted_terms
                                        ? "border-gold bg-gold/10"
                                        : "border-[#e07a5f]/40 bg-[#e07a5f]/5"
                                }`}
                                data-testid="partner-terms-label"
                            >
                                <input
                                    type="checkbox"
                                    checked={form.accepted_terms}
                                    onChange={(e) =>
                                        update(
                                            "accepted_terms",
                                            e.target.checked,
                                        )
                                    }
                                    className="mt-1 h-4 w-4 accent-[color:var(--ig-gold)]"
                                    data-testid="partner-terms"
                                />
                                <span className="text-sm text-forest leading-relaxed">
                                    <span className="font-semibold tracking-wide uppercase text-[0.65rem] text-gold mr-1">
                                        Partner Terms
                                    </span>
                                    I authorise Director K. to review this
                                    intel, contact the named representative,
                                    and store the inquiry in EIGOO's secure
                                    manifest.
                                </span>
                            </label>

                            <div className="flex items-center justify-between">
                                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                                    Routes to Director K.
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-pill btn-primary disabled:opacity-50"
                                    data-testid="partner-submit-btn"
                                >
                                    {submitting ? (
                                        "Filing…"
                                    ) : (
                                        <>
                                            Send Inquiry{" "}
                                            <ArrowRight size={14} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}

const inputCls =
    "w-full bg-cream border border-forest/15 rounded-sm px-3 py-2.5 text-sm text-forest focus:border-gold outline-none";

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[0.6rem] tracking-[0.3em] uppercase text-brown">
                {label}
            </span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
